import { gql, useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, BookOpen, Code2, Database, FileJson, Server, ArrowRight } from 'lucide-react';

const GET_GQL_SCHEMA = gql`
  query GetGqlSchema {
    getGqlSchema
  }
`;

type Endpoint = {
  kind: 'query' | 'mutation' | 'subscription';
};

type SchemaPayload = {
  endpoints?: Endpoint[];
  types?: Record<string, unknown>;
};

const parseSchemaPayload = (raw?: string | null): SchemaPayload => {
  if (!raw) {
    return { endpoints: [], types: {} };
  }
  try {
    const parsed = JSON.parse(raw);
    const endpoints = Array.isArray(parsed?.endpoints) ? parsed.endpoints : [];
    const types = parsed?.types && typeof parsed.types === 'object' ? parsed.types : {};
    return { endpoints, types };
  } catch (error) {
    return { endpoints: [], types: {} };
  }
};

export const AdminDevCenter = () => {
  const { data, loading, error } = useQuery<{ getGqlSchema: string }>(GET_GQL_SCHEMA);
  const payload = useMemo(() => parseSchemaPayload(data?.getGqlSchema), [data?.getGqlSchema]);

  const counts = useMemo(() => {
    const endpoints = payload.endpoints || [];
    const types = payload.types || {};
    const result = {
      queries: 0,
      mutations: 0,
      subscriptions: 0,
      types: Object.keys(types).length
    };
    endpoints.forEach((entry) => {
      if (entry.kind === 'query') {
        result.queries += 1;
        return;
      }
      if (entry.kind === 'mutation') {
        result.mutations += 1;
        return;
      }
      if (entry.kind === 'subscription') {
        result.subscriptions += 1;
      }
    });
    return result;
  }, [payload.endpoints, payload.types]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-12">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <Activity className="h-8 w-8 animate-pulse text-blue-600" />
          <p className="text-sm font-medium">Initializing developer environment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
            <Server className="h-10 w-10 text-red-500" />
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-red-900">Connection Failed</h3>
              <p className="text-sm text-red-700">Unable to retrieve schema information.</p>
            </div>
            <p className="text-xs font-mono text-red-600 bg-red-100 px-3 py-1 rounded-full">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      
      {/* Header Section */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Developer Center</h1>
          <p className="text-gray-500 mt-1">
            Manage API schemas, explore documentation, and monitor system types.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild>
            <Link to="/admin/dev/ui-style-guide" id="admin-dev-ui-style-guide-button">
              <ArrowRight className="h-4 w-4" />
              UI Style Guide Demo
            </Link>
          </Button>
          <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
            <span className="h-2 w-2 rounded-full bg-blue-500 mr-2" />
            Environment: Dev
          </Badge>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 rounded-lg">
              <Database className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Types</p>
              <h3 className="text-2xl font-bold text-gray-900">{counts.types}</h3>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <Activity className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Queries</p>
              <h3 className="text-2xl font-bold text-gray-900">{counts.queries}</h3>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-amber-50 rounded-lg">
              <Server className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Mutations</p>
              <h3 className="text-2xl font-bold text-gray-900">{counts.mutations}</h3>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Subscriptions</p>
              <h3 className="text-2xl font-bold text-gray-900">{counts.subscriptions}</h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Actions */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* API Documentation Card */}
        <Link to="/admin/dev/guide" className="group block h-full">
          <Card className="h-full border-gray-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all duration-200">
            <CardHeader>
              <div className="mb-2 h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-200">
                <BookOpen className="h-5 w-5 text-blue-600 group-hover:text-white" />
              </div>
              <CardTitle className="flex items-center justify-between text-xl text-gray-900">
                API Guide
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
              </CardTitle>
              <CardDescription className="text-gray-500">
                Comprehensive documentation for all available GraphQL queries, mutations, and subscriptions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-200">
                  Explorer
                </Badge>
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-200">
                  Schema Definition
                </Badge>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Type Explorer Card */}
        <Link to="/admin/dev/types" className="group block h-full">
          <Card className="h-full border-gray-200 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all duration-200">
            <CardHeader>
              <div className="mb-2 h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-600 transition-colors duration-200">
                <Code2 className="h-5 w-5 text-indigo-600 group-hover:text-white" />
              </div>
              <CardTitle className="flex items-center justify-between text-xl text-gray-900">
                Type System
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
              </CardTitle>
              <CardDescription className="text-gray-500">
                Deep dive into the data model. Explore scalars, enums, input objects, and interfaces.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-200">
                  {counts.types} Definitions
                </Badge>
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-200">
                  Graph Hierarchy
                </Badge>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Quick Links / Footer like section */}
        <div className="grid gap-4 md:grid-cols-3">
            <Card className="border-gray-200 bg-gray-50 shadow-none">
                <CardContent className="p-4 flex items-center gap-3">
                    <FileJson className="h-5 w-5 text-gray-400" />
                    <div>
                        <p className="text-sm font-medium text-gray-700">JSON Export</p>
                        <p className="text-xs text-gray-500">Download schema as JSON</p>
                    </div>
                </CardContent>
            </Card>
            <Card className="border-gray-200 bg-gray-50 shadow-none">
                <CardContent className="p-4 flex items-center gap-3">
                    <Server className="h-5 w-5 text-gray-400" />
                    <div>
                        <p className="text-sm font-medium text-gray-700">Server Status</p>
                        <p className="text-xs text-gray-500">All systems operational</p>
                    </div>
                </CardContent>
            </Card>
      </div>
    </div>
  );
};

